import _ from "lodash";
import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useWindowSize } from "react-use";
import styled from "styled-components";

import { Button, TextInput, Toolbar } from "@examspark/common-ui";
import {
  IconPageNextLine,
  IconPagePreviousLine,
} from "@examspark/common-ui/dist/components/icons";
import {
  JumpBar,
  JumpBarContext,
} from "@examspark/common-ui/dist/components/utils/JumpBar";
import { useDebounce } from "@examspark/common-ui/dist/hooks/useDebounce";

import { WindowContext } from "../common/AppWrapper";

import { AlignmentViewData, computeVisibility } from "./AlignmentViewData";
import AlignmentViewSection from "./AlignmentViewSection";
import { CheckboxGridFilterButton } from "./CheckboxGridFilterButton";

interface StyledProps {
  columnCount?: number;
}

const Styled = styled.div<StyledProps>`
  --min-col-width: 30rem;
  --col-count: ${(props) => props.columnCount ?? 1};
`;

interface StyledTableProps {
  offsetHeight?: number;
  device?: string;
}

const StyledTable = styled.div<StyledTableProps>`
  min-width: 0;

  // this will need to  be adjusted if you change the size of anything
  // in here
  --section-title-offset: 80px;

  table {
    width: 100%;
  }

  td {
    min-width: var(--min-col-width);
    vertical-align: top;
    padding: 1.5rem 1.5rem;
    border-bottom: solid 1px #ddd;
    box-sizing: border-box;

    line-height: 1.6em;
    color: #333;
    border-left: solid 1px #ddd;
  }

  td:first-child {
    border-left: none;
  }

  thead {
    position: sticky;
    background-color: #fff;
    top: 0;

    z-index: 1;
  }

  thead tr th {
    color: #333;
    font-weight: 500;
    height: 4rem;
    border-bottom: solid 1px #ddd;
    color: #431f99;
  }

  thead tr td {
    border-top: none;
    padding: 1rem 1rem;
  }
`;

const ToolbarHeader = styled.th`
  & > div {
    position: sticky;
    left: 0;

    padding: 0rem 1rem;

    max-width: calc(100dvw - 4rem);
  }
`;

const GradeHeaderText = styled.span`
  position: sticky;
  left: 2ch;
  right: 2ch;
`;

const StyledJumpBar = styled(JumpBar)`
  flex-grow: 1;
`;

type Props = {
  data: AlignmentViewData | undefined;
  initialColumns?: number[];
  toolbar?: ReactNode;
  navigation?: ReactNode;
  hideNavigation?: boolean;
};

const AlignmentView = ({
  data,
  initialColumns,
  toolbar,
  navigation,
  hideNavigation,
}: Props) => {
  const [jumpBarEntries, setJumpBarEntries] = useState({});
  const { device } = React.useContext(WindowContext);
  const [filterText, setFilterText] = useState<string>("");

  const [selectedColumnsIndex, setSelectedColumnsIndex] = useState<number[]>(
    initialColumns ?? _.range(0, data?.columns?.length ?? 0),
  );

  useEffect(() => {
    if (initialColumns == null) return;
    setSelectedColumnsIndex(initialColumns);
  }, [initialColumns]);

  // this is the index of the left column the user has scrolled to using the
  // horizontal scroll buttons
  const [horizontalScrollPosition, setHorizontalScrollPosition] =
    useState<number>(0);

  // minimum width required so that (my) ipad can fit 3 columns
  const minWidthPerColumn = 390;
  const width = useWindowSize().width;

  const effectiveColumnCount = Math.floor(width / minWidthPerColumn) || 1;

  const debouncedText = useDebounce<string>(filterText, 1000);

  const visibleColumnsIndex = selectedColumnsIndex.filter(
    (i, idx) =>
      idx >= horizontalScrollPosition &&
      idx < horizontalScrollPosition + effectiveColumnCount,
  );

  const visibility = useMemo(
    () =>
      computeVisibility(
        data,
        visibleColumnsIndex,
        selectedColumnsIndex,
        debouncedText,
      ),
    [data, debouncedText, selectedColumnsIndex, visibleColumnsIndex],
  );

  const onSelectedColumnsChange = useCallback(
    (selected: string[]) => {
      setSelectedColumnsIndex(
        selected
          .sort((a: any, b: any) => parseInt(a, 10) - parseInt(b, 10))
          .map((n) => parseInt(n, 10)),
      );
      if (selected.length < horizontalScrollPosition + effectiveColumnCount) {
        setHorizontalScrollPosition(
          Math.max(0, selected.length - effectiveColumnCount),
        );
      }
    },
    [effectiveColumnCount, horizontalScrollPosition],
  );

  const scrollToNextColumn = () => {
    setHorizontalScrollPosition((prev) => {
      const next = prev + 1;
      if (next > selectedColumnsIndex.length - effectiveColumnCount) {
        return prev;
      }

      return next;
    });
  };

  const scrollToPreviousColumn = () => {
    setHorizontalScrollPosition((prev) => {
      const next = prev - 1;
      if (next < 0) {
        return prev;
      }

      return next;
    });
  };

  return (
    <JumpBarContext.Provider
      value={{
        entries: jumpBarEntries,
        setEntries: setJumpBarEntries,
      }}
    >
      <Styled columnCount={visibleColumnsIndex.length}>
        <StyledTable device={device}>
          <table cellSpacing={0} cellPadding={0}>
            <thead>
              <tr>
                <ToolbarHeader colSpan={99}>
                  <Toolbar>
                    {!hideNavigation &&
                      (navigation ?? <StyledJumpBar menuPosition="fixed" />)}
                    <Toolbar.Right>
                      <TextInput
                        placeholder="Filter Text..."
                        style={{
                          flexBasis: "20rem",
                          minWidth: "10rem",
                        }}
                        value={filterText}
                        onChange={(e) => setFilterText(e.target.value)}
                        autoCapitalize="off"
                      />
                      {toolbar}
                      <CheckboxGridFilterButton
                        options={data?.columns?.map((column, index) => ({
                          label: column.label,
                          value: index.toString(),
                        }))}
                        value={selectedColumnsIndex.map((n) => n.toString())}
                        onChange={onSelectedColumnsChange}
                      />
                      <Button
                        title="Scroll to Previous Column"
                        disabled={horizontalScrollPosition === 0}
                        onClick={() => scrollToPreviousColumn()}
                        variant="plain"
                      >
                        <IconPagePreviousLine width="16" />
                      </Button>
                      <Button
                        title="Scroll to Next Column"
                        disabled={
                          horizontalScrollPosition >=
                          selectedColumnsIndex.length - effectiveColumnCount
                        }
                        onClick={() => scrollToNextColumn()}
                        variant="plain"
                      >
                        <IconPageNextLine width="16" />
                      </Button>
                    </Toolbar.Right>
                  </Toolbar>
                </ToolbarHeader>
              </tr>
              <tr>
                {data?.columns?.map((col, idx) => {
                  if (!visibleColumnsIndex.includes(idx)) {
                    return null;
                  }
                  return (
                    <th
                      key={col.value}
                      style={{
                        width: "calc(100% / var(--col-count)",
                      }}
                    >
                      <GradeHeaderText>{col.label}</GradeHeaderText>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {data?.sections?.map((section, i) => {
                return (
                  <AlignmentViewSection
                    key={section.id}
                    section={section}
                    label={section.title ?? `Section ${i + 1}`}
                    visibility={visibility.sections?.[i]}
                  />
                );
              })}
            </tbody>
          </table>
        </StyledTable>
      </Styled>
    </JumpBarContext.Provider>
  );
};

export default AlignmentView;
